import { Card, Button, Icon } from '../atoms'
import { SettingsPanel, StatusDisplay, FeatureCard } from '../molecules'
import type { AppSettings, AppState } from '../../types'

export interface ControlPanelProps {
  state: AppState
  settings: AppSettings
  onStart: () => void
  onStop: () => void
  onTest: () => void
  onRefresh?: () => void
  onSettingsChange: (settings: Partial<AppSettings>) => void
  onClearPOIs?: () => void
  onRefreshPOIs?: () => void
  onClearPinnedLocations?: () => void
  poiCount?: number
  pinnedCount?: number
}

export const ControlPanel = ({
  state,
  settings,
  onStart,
  onStop,
  onTest,
  onRefresh,
  onSettingsChange,
  onClearPOIs,
  onRefreshPOIs,
  onClearPinnedLocations,
  poiCount = 0,
  pinnedCount = 0,
}: ControlPanelProps) => {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Main Controls */}
      <Card>
        <div className="space-y-2.5 sm:space-y-3">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5">
            <Button
              onClick={onStart}
              disabled={state.isTracking}
              className="flex-1 text-sm sm:text-base py-3 sm:py-3.5 font-semibold min-h-[44px] touch-manipulation"
              aria-label="Start GPS tracking and begin detecting points of interest"
              title="Start tracking your location and discover nearby points of interest"
            >
              Start Tracking
            </Button>
            <Button
              onClick={onStop}
              disabled={!state.isTracking}
              variant="secondary"
              className="flex-1 text-sm sm:text-base py-3 sm:py-3.5 font-semibold min-h-[44px] touch-manipulation"
              aria-label="Stop GPS tracking"
              title="Stop tracking your location"
            >
              Stop
            </Button>
          </div>
          {state.isTracking && onRefresh && (
            <Button
              onClick={onRefresh}
              variant="secondary"
              className="w-full text-xs sm:text-sm py-2.5 sm:py-2 flex items-center justify-center gap-2 min-h-[40px] touch-manipulation"
              aria-label="Manually refresh your current location"
              title="Manually update your location and search for new points of interest"
            >
              <Icon name="location" className="w-4 h-4 flex-shrink-0" />
              <span>Refresh Location</span>
            </Button>
          )}
          <Button
            onClick={onTest}
            variant="secondary"
            className="w-full text-xs sm:text-sm py-2.5 sm:py-2 min-h-[40px] touch-manipulation"
            aria-label="Test speech synthesis"
            title="Test if the voice narration is working"
          >
            Test Voice
          </Button>
        </div>
      </Card>

      {/* Settings */}
      <FeatureCard
        title="Settings"
        icon={<Icon name="info" className="w-5 h-5" />}
      >
        <SettingsPanel 
          settings={settings} 
          onSettingsChange={onSettingsChange}
          onClearPOIs={onClearPOIs}
          onRefreshPOIs={onRefreshPOIs}
          onClearPinnedLocations={onClearPinnedLocations}
          poiCount={poiCount}
          pinnedCount={pinnedCount}
        />
      </FeatureCard>

      {/* Status */}
      <FeatureCard
        title="Status"
        icon={<Icon name="location" className="w-5 h-5" />}
      >
        <StatusDisplay
          status={state.status}
          lastLocation={state.lastLocation}
          heading={state.heading}
          lastPOI={state.lastPOI}
          lastSpoken={state.lastSpoken}
        />
      </FeatureCard>
    </div>
  )
}
